/**
 * Os seis painéis do quadro por eixos (E78.F3.T03).
 *
 * Cada painel abre com a frase do que aquele eixo confronta — a pergunta que o
 * quadro antigo não respondia — e depois mostra APENAS o que o backend já
 * devolveu. Nenhuma alegação nova nasce aqui: as peças que sabem o que pode ser
 * afirmado (assinatura embutida, cluster, coordenada, linha do tempo) são as
 * mesmas do quadro item a item.
 *
 * As colunas "Declarado no protocolo × Apurado na peça" substituem o par
 * Esperado/Obtido, e só aparecem quando algum item do eixo traz os dois lados:
 * no caso real, cinco das sete linhas de identidade exibiam "—" nas duas
 * colunas, dizendo ao perito que havia um confronto onde não havia.
 */
import type { ReactNode } from 'react';

import {
  Badge,
  MonoData,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ItemVerificacaoData, ResultadoExameEletronicoData } from '@/lib/exame_eletronico';

import { AssinaturaDigital } from './QuadroVerificacaoAssinatura';
import { ClusterCadastral, CoordenadaAvaliada } from './QuadroVerificacaoCluster';
import {
  Ausencias,
  LinhaDoTempo,
  ListaDeIps,
  RedFlags,
  SituacaoBadge,
  Titulo,
} from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;
const tq = ptBR.exameEletronico.quadro;

export interface PainelDoEixoProps {
  resultado: ResultadoExameEletronicoData;
}

function Frase({ children }: { children: string }): JSX.Element {
  return <p className="max-w-prose text-body-sm text-fg-muted">{children}</p>;
}

/** Rótulo do item por chave; chave desconhecida cai no texto da própria chave. */
function rotuloDoItem(chave: string): string {
  const itens: Record<string, string> = tq.itens;
  return itens[chave] ?? chave;
}

/** O item tem os dois lados do confronto (ou ao menos um deles) para mostrar. */
function temConfronto(item: ItemVerificacaoData): boolean {
  return Boolean(item.esperado) || Boolean(item.obtido);
}

function CelulaConfronto({ item }: { item: ItemVerificacaoData }): JSX.Element {
  if (!temConfronto(item)) {
    return <span className="text-body-sm text-fg-muted">{item.detalhe ?? '—'}</span>;
  }
  return (
    <div className="space-y-sc-1">
      {item.esperado ? (
        <p>
          <span className="text-caption text-fg-dim">{t.declarado}</span>{' '}
          <MonoData>{item.esperado}</MonoData>
        </p>
      ) : null}
      {item.obtido ? (
        <p>
          <span className="text-caption text-fg-dim">{t.apurado}</span>{' '}
          <MonoData>{item.obtido}</MonoData>
        </p>
      ) : null}
      {item.detalhe ? <p className="text-body-sm text-fg-muted">{item.detalhe}</p> : null}
    </div>
  );
}

export function FatosDoEixo({
  itens,
  testId,
  children,
}: {
  itens: readonly ItemVerificacaoData[];
  testId: string;
  children?: ReactNode;
}): JSX.Element {
  const algumConfronto = itens.some(temConfronto);
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.colunaItem}</TableHead>
            <TableHead>{t.colunaSituacao}</TableHead>
            <TableHead>{algumConfronto ? t.colunaConfronto : t.colunaDetalhe}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {itens.map((item) => (
            <TableRow key={item.chave} data-testid={`${testId}-${item.chave}`}>
              <TableCell className="whitespace-nowrap">{rotuloDoItem(item.chave)}</TableCell>
              <TableCell>
                <SituacaoBadge situacao={item.situacao} />
              </TableCell>
              <TableCell className="max-w-[28rem] break-all">
                <CelulaConfronto item={item} />
              </TableCell>
            </TableRow>
          ))}
          {children}
        </TableBody>
      </Table>
    </div>
  );
}

/** Estágios externos daquele eixo, pelas chaves que o backend usa. */
function estagios(
  resultado: ResultadoExameEletronicoData,
  chaves: readonly string[],
): ItemVerificacaoData[] {
  return resultado.enriquecimento.filter((item) => chaves.includes(item.chave));
}

/**
 * A assinatura embutida em UMA linha quando o veredito é "não se aplica".
 *
 * O caso real gastava sete linhas para dizer que o arquivo não trazia
 * assinatura criptográfica. Nos demais vereditos o bloco completo continua
 * inteiro — ali cada linha responde por um fato apurado.
 */
function LinhaAssinaturaNaoSeAplica(): JSX.Element {
  return (
    <TableRow data-testid="quadro-integridade-assinatura-embutida">
      <TableCell className="whitespace-nowrap">{tq.itens.assinatura_digital_presente}</TableCell>
      <TableCell>
        <Badge variant="neutral">{tq.assinaturaDigitalVereditoOpcoes.nao_se_aplica}</Badge>
      </TableCell>
      <TableCell className="max-w-[28rem] text-body-sm text-fg-muted">
        {t.assinaturaNaoSeAplica}
      </TableCell>
    </TableRow>
  );
}

export function PainelIntegridade({ resultado }: PainelDoEixoProps): JSX.Element {
  const naoSeAplica = resultado.assinatura_digital_veredito === 'nao_se_aplica';
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-integridade">
      <Frase>{t.confronta.integridade}</Frase>
      <FatosDoEixo itens={resultado.integridade} testId="quadro-integridade">
        {naoSeAplica ? <LinhaAssinaturaNaoSeAplica /> : null}
      </FatosDoEixo>
      {naoSeAplica ? null : (
        <AssinaturaDigital
          apurado={resultado.assinatura_digital}
          veredito={resultado.assinatura_digital_veredito}
        />
      )}
    </div>
  );
}

export function PainelIdentidade({ resultado }: PainelDoEixoProps): JSX.Element {
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-identidade">
      <Frase>{t.confronta.identidade}</Frase>
      <FatosDoEixo itens={resultado.identidade} testId="quadro-identidade" />
    </div>
  );
}

export function PainelTrilha({ resultado }: PainelDoEixoProps): JSX.Element {
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-trilha">
      <Frase>{t.confronta.trilha}</Frase>
      <LinhaDoTempo
        eventos={resultado.linha_do_tempo}
        sequenciaIlogica={resultado.sequencia_ilogica}
      />
    </div>
  );
}

export function PainelRede({ resultado }: PainelDoEixoProps): JSX.Element {
  const externos = estagios(resultado, ['classificacao_ip']);
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-rede">
      <Frase>{t.confronta.rede}</Frase>
      {externos.length > 0 ? (
        <FatosDoEixo itens={externos} testId="quadro-enriquecimento-rede" />
      ) : null}
      <ListaDeIps ips={resultado.ips} />
    </div>
  );
}

export function PainelLocalizacao({ resultado }: PainelDoEixoProps): JSX.Element {
  const externos = estagios(resultado, ['cluster_cadastral', 'geocodificacao']);
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-localizacao">
      <Frase>{t.confronta.localizacao}</Frase>
      <section data-testid="quadro-coordenada">
        <div className="flex items-center gap-sc-2">
          <Titulo>{tq.coordenada}</Titulo>
          <span className="mb-sc-2">
            <SituacaoBadge situacao={resultado.coordenada_situacao} />
          </span>
        </div>
        {resultado.coordenada ? <CoordenadaAvaliada coordenada={resultado.coordenada} /> : null}
      </section>
      {externos.length > 0 ? (
        <FatosDoEixo itens={externos} testId="quadro-enriquecimento-localizacao" />
      ) : null}
      <ClusterCadastral cluster={resultado.cluster} />
    </div>
  );
}

export function PainelAlertas({ resultado }: PainelDoEixoProps): JSX.Element {
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-alertas">
      <Frase>{t.confronta.alertas}</Frase>
      <RedFlags flags={resultado.red_flags} />
      <Ausencias ausencias={resultado.ausencias} />
    </div>
  );
}
